import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import api from '../services/api';

const accessLevelOptions = [
  { value: 'global', label: 'Global Access' },
  { value: 'organization', label: 'Organization Access' },
  { value: 'entity', label: 'Entity Access' }
];

const AssignUserDialog = ({ open, onClose, entityId, entityName, onUserAssigned }) => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [accessLevel, setAccessLevel] = useState('entity');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch available users that are not already assigned to this entity
  useEffect(() => {
    if (open && entityId) {
      setLoading(true);
      api.get('/users/available', { params: { entityId } })
        .then(response => {
          setUsers(response.data.data || []);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching available users:', err);
          setError('Failed to load available users. Please try again.');
          setLoading(false);
        });
    }
  }, [open, entityId]);

  const handleSubmit = async () => {
    if (!selectedUserId) {
      setError('Please select a user to assign');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await api.post(`/users/${selectedUserId}/assign`, {
        entityId,
        accessLevel
      });
      
      setSuccess(true);
      setLoading(false);
      
      // Reset form after successful submission
      setTimeout(() => {
        if (onUserAssigned) onUserAssigned();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error assigning user:', err);
      setError(err.response?.data?.message || 'Failed to assign user. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUserId('');
    setAccessLevel('entity');
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign User to {entityName || 'Entity'}
      </DialogTitle>
      <DialogContent>
        {loading && !error && !success ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                User successfully assigned to {entityName}!
              </Alert>
            )}
            
            {!success && (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Assign a user to this entity with the appropriate access level.
                </Typography>
                
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="user-select-label">Select User</InputLabel>
                  <Select
                    labelId="user-select-label"
                    value={selectedUserId}
                    label="Select User"
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    disabled={loading}
                  >
                    {users.length === 0 ? (
                      <MenuItem disabled value="">
                        No available users
                      </MenuItem>
                    ) : (
                      users.map(user => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} ({user.email})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel id="access-level-label">Access Level</InputLabel>
                  <Select
                    labelId="access-level-label"
                    value={accessLevel}
                    label="Access Level"
                    onChange={(e) => setAccessLevel(e.target.value)}
                    disabled={loading}
                  >
                    {accessLevelOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {!success && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading || !selectedUserId}
          >
            {loading ? <CircularProgress size={24} /> : 'Assign User'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssignUserDialog;
